'use client';

/**
 * Finalize Stage View
 *
 * Creates a Pull Request for the completed work.
 * This stage is entered after the user accepts changes during Review.
 * The request will close when the PR is merged or closed in GitHub.
 */

import type { Request } from '@cortex/database';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  Center,
  Grid,
  Heading,
  Inline,
  MetadataField,
  Spinner,
  Stack,
  Text,
} from '@cortex/ui';
import {
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  GitMerge,
  GitPullRequest,
  RefreshCw,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';

import type { FinalizeStageViewProps } from './types';

type PRCreationStatus = 'idle' | 'creating' | 'ready' | 'failed';
type PRCheckStatus = 'idle' | 'checking' | 'open' | 'merged' | 'closed';

interface PRState {
  creationStatus: PRCreationStatus;
  checkStatus: PRCheckStatus;
  url: string | null;
  error: string | null;
  checkMessage: string | null;
}

export default function FinalizeStageView({
  request,
  onRequestUpdate,
}: FinalizeStageViewProps) {
  const fetchWithAuth = useFetchWithAuth();
  const hasTriggeredCreation = useRef(false);

  // PR state
  const [prState, setPrState] = useState<PRState>(() => {
    const existingUrl = (request as Request & { pr_url?: string | null })
      .pr_url;
    return {
      creationStatus: existingUrl ? 'ready' : 'idle',
      checkStatus: 'idle',
      url: existingUrl ?? null,
      error: null,
      checkMessage: null,
    };
  });

  // Trigger PR creation
  const createPR = useCallback(async () => {
    setPrState((prev) => ({
      ...prev,
      creationStatus: 'creating',
      error: null,
    }));

    try {
      const response = await fetchWithAuth(
        `/requests/${request.id}/pr/create`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error?.message || 'Failed to create pull request');
      }

      // API responses wrap data in { data: ... }
      const { data } = json;

      setPrState((prev) => ({
        ...prev,
        creationStatus: 'ready',
        url: data.prUrl,
        error: null,
      }));
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create pull request';
      setPrState((prev) => ({
        ...prev,
        creationStatus: 'failed',
        error: errorMessage,
      }));
    }
  }, [request.id, fetchWithAuth]);

  // Check PR status in GitHub
  const checkPRStatus = useCallback(async () => {
    setPrState((prev) => ({
      ...prev,
      checkStatus: 'checking',
      checkMessage: null,
    }));

    try {
      const response = await fetchWithAuth(`/requests/${request.id}/pr/status`);
      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.error?.message || 'Failed to check PR status');
      }

      // API responses wrap data in { data: ... }
      const { data } = json;

      if (data.status === 'merged' || data.status === 'closed') {
        setPrState((prev) => ({
          ...prev,
          checkStatus: data.status,
          checkMessage: data.message,
        }));

        // Refresh request data since it's now closed
        if (onRequestUpdate) {
          // Fetch updated request
          const requestResponse = await fetchWithAuth(
            `/requests/${request.id}`
          );
          if (requestResponse.ok) {
            const requestJson = await requestResponse.json();
            onRequestUpdate(requestJson.data.request);
          }
        }
      } else {
        setPrState((prev) => ({
          ...prev,
          checkStatus: 'open',
          checkMessage: data.message,
        }));
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to check PR status';
      setPrState((prev) => ({
        ...prev,
        checkStatus: 'idle',
        checkMessage: errorMessage,
      }));
    }
  }, [request.id, onRequestUpdate, fetchWithAuth]);

  // Auto-trigger PR creation on mount if not already created
  useEffect(() => {
    if (prState.creationStatus === 'idle' && !hasTriggeredCreation.current) {
      hasTriggeredCreation.current = true;
      void createPR();
    }
  }, [prState.creationStatus, createPR]);

  const isReady = prState.creationStatus === 'ready';
  const isChecking = prState.checkStatus === 'checking';
  const wasMergedOrClosed =
    prState.checkStatus === 'merged' || prState.checkStatus === 'closed';

  return (
    <Stack gap="lg">
      {/* Header */}
      <Alert
        variant={
          wasMergedOrClosed || isReady
            ? 'success'
            : prState.creationStatus === 'failed'
              ? 'destructive'
              : 'default'
        }
        className="p-6"
      >
        <Inline gap="sm" align="center">
          {wasMergedOrClosed ? (
            <GitMerge className="text-success h-6 w-6" />
          ) : isReady ? (
            <CheckCircle className="text-success h-6 w-6" />
          ) : prState.creationStatus === 'failed' ? (
            <AlertTriangle className="text-error h-6 w-6" />
          ) : (
            <Spinner size="md" />
          )}
          <Stack gap="xs">
            <Heading level={2} className="text-lg font-semibold">
              {wasMergedOrClosed
                ? prState.checkStatus === 'merged'
                  ? 'Pull Request Merged'
                  : 'Pull Request Closed'
                : isReady
                  ? 'Pull Request Created'
                  : prState.creationStatus === 'failed'
                    ? 'Failed to Create Pull Request'
                    : 'Creating Pull Request'}
            </Heading>
            <Text color="muted" size="sm">
              {wasMergedOrClosed
                ? prState.checkMessage || 'Request has been closed.'
                : isReady
                  ? 'Your changes are ready for code review.'
                  : prState.creationStatus === 'failed'
                    ? prState.error ||
                      'An error occurred while creating the PR.'
                    : 'Setting up your pull request...'}
            </Text>
          </Stack>
        </Inline>
      </Alert>

      {/* PR Card */}
      <Card className="p-6">
        <Inline justify="between" align="center">
          <Inline gap="md" align="center">
            <Center
              className={`h-12 w-12 rounded-lg ${
                wasMergedOrClosed
                  ? 'bg-success/10'
                  : isReady
                    ? 'bg-success/10'
                    : prState.creationStatus === 'failed'
                      ? 'bg-error/10'
                      : 'bg-surface-3'
              }`}
            >
              {prState.creationStatus === 'creating' ||
              prState.creationStatus === 'idle' ? (
                <Spinner size="md" />
              ) : wasMergedOrClosed ? (
                <GitMerge className="text-success h-6 w-6" />
              ) : isReady ? (
                <GitPullRequest className="text-success h-6 w-6" />
              ) : (
                <AlertTriangle className="text-error h-6 w-6" />
              )}
            </Center>
            <Stack gap="xs">
              <Text weight="semibold">{request.title}</Text>
              <Text color="muted" size="sm">
                {prState.creationStatus === 'idle' && 'Preparing...'}
                {prState.creationStatus === 'creating' &&
                  'Creating pull request...'}
                {prState.creationStatus === 'failed' && 'Creation failed'}
                {isReady &&
                  !wasMergedOrClosed &&
                  (prState.checkStatus === 'open'
                    ? 'PR is open - awaiting review'
                    : 'Ready for review')}
                {prState.checkStatus === 'merged' && 'PR was merged'}
                {prState.checkStatus === 'closed' &&
                  'PR was closed without merging'}
              </Text>
            </Stack>
          </Inline>

          <Inline gap="sm" align="center">
            {prState.creationStatus === 'failed' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void createPR()}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            )}
            {isReady && !wasMergedOrClosed && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void checkPRStatus()}
                loading={isChecking}
              >
                {!isChecking && <RefreshCw className="mr-2 h-4 w-4" />}
                Check Status
              </Button>
            )}
            {prState.url && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(prState.url!, '_blank')}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open PR
              </Button>
            )}
          </Inline>
        </Inline>
      </Card>

      {/* Request Summary */}
      <Card className="p-6">
        <Heading level={3} className="font-semibold">
          Request Summary
        </Heading>

        <Stack gap="md" className="mt-4">
          {request.description && (
            <MetadataField
              label="Description"
              valueColor="secondary"
              valueSize="sm"
            >
              {request.description}
            </MetadataField>
          )}

          <Grid columns={2} gap="md">
            <MetadataField label="Type" valueClassName="mt-1 capitalize">
              {request.type}
            </MetadataField>
            <MetadataField label="Priority" valueClassName="mt-1 capitalize">
              {request.priority}
            </MetadataField>
          </Grid>
        </Stack>
      </Card>

      {/* Status message */}
      {prState.checkMessage && prState.checkStatus === 'open' && (
        <Alert variant="default" className="border-info/30 bg-info/5">
          <AlertDescription className="text-info">
            {prState.checkMessage}
          </AlertDescription>
        </Alert>
      )}

      {/* Next Steps */}
      {isReady && !wasMergedOrClosed && !prState.checkMessage && (
        <Alert variant="default" className="border-info/30 bg-info/5">
          <AlertDescription className="text-info">
            <Text as="strong" weight="bold">
              Next steps:
            </Text>{' '}
            Review and merge the pull request in GitHub. Click &quot;Check
            Status&quot; to update this page after the PR is merged or closed.
          </AlertDescription>
        </Alert>
      )}
    </Stack>
  );
}
